import React, { useMemo } from "react";
import useAuth from "hooks/useAuth";
import { hasDpatFullScoreAccess, isDpatDistrictRestrictedViewer } from "config/contextModules";
import { useGetScoreSheetSummary } from "service/comment.service";
import {
  buildSubIndicatorDetailLists,
  compactCodeFromSlug,
  DPAT_SLUG_TO_TABLE_KEY,
  formatManualScoreDisplay,
  hasEnteredManualScore,
  indicatorKindFromSlug,
  MANUAL_SCORE_FIELDS,
  readIndicatorManualScore,
  resolveRoleStandingCiStatuses,
  resolveRoleStandingNumericScores,
} from "utils/dpatManualScoreHelpers";
import { DPAT_SHEET_PAGE_LABELS } from "config/dpatSheetPaginationLabels";

const cellBorder = { border: "1px solid #000", padding: "6px 8px" };
const THEMATIC_BY_SLUG = Object.keys(DPAT_SLUG_TO_TABLE_KEY).reduce((map, slug, index) => {
  const label = String(DPAT_SHEET_PAGE_LABELS[index] || "").replace(/^Annex \d+\s+—\s*/, "").trim();
  if (label) map[slug] = label;
  return map;
}, {});

function isSubtotal(item) {
  return String(item?.no || "").toLowerCase().includes("sub-total");
}

function itemHasComplaintResolutionInput(item) {
  if (!item || isSubtotal(item)) return false;
  if (item.petitionCommitteeHasOverride) return true;
  return hasEnteredManualScore(readIndicatorManualScore(item, MANUAL_SCORE_FIELDS.PETITION_COMMITTEE));
}

function parseIndicatorCode(no) {
  const text = String(no || "").trim();
  if (!text || text.toLowerCase().includes("sub-total")) return null;
  const pairs = [...text.matchAll(/(\d+)\.(\d+)/g)];
  if (pairs.length) {
    const last = pairs[pairs.length - 1];
    const major = last[1];
    if (last[2] !== "0") return { type: "sub", key: `${major}.${last[2]}`, major };
    return { type: "parent", key: major, major };
  }
  if (/^\d+$/.test(text)) return { type: "parent", key: text, major: text };
  return null;
}

function buildSubColumns() {
  return Object.keys(DPAT_SLUG_TO_TABLE_KEY)
    .map((slug) => {
      const kind = indicatorKindFromSlug(slug);
      const subKey = compactCodeFromSlug(slug);
      const [major, minor] = String(subKey).split(".");
      if (!kind || !major || !minor) return null;
      const prefix = kind === "CI" ? "CI" : kind;
      return {
        slug,
        kind,
        subKey,
        major,
        label: `${prefix} ${subKey}`,
        parentLabel: `${prefix} ${major}`,
      };
    })
    .filter(Boolean);
}

function findSubRow(items, subKey) {
  const wanted = String(subKey || "").trim();
  return (items || []).find((item) => {
    if (!item || isSubtotal(item) || item.childScoreAggregate) return false;
    const parsed = parseIndicatorCode(item.no);
    return parsed?.key === wanted || String(item.no || "").trim() === wanted || String(item.slug || "").endsWith(`-${wanted}`);
  }) || null;
}

function resolveDistrictQueryId(district, districtId) {
  if (districtId != null && districtId !== "") return districtId;
  if (district?.value != null && typeof district.value !== "object") return district.value;
  return district?.value?.id ?? district?.id ?? district?.value;
}

function mergeSubLists(savedLists, liveLists) {
  const merged = {};
  ["CI", "SDI", "PI"].forEach((kind) => {
    const byCode = new Map();
    (savedLists?.[kind] || []).forEach((item) => byCode.set(String(item.no), item));
    (liveLists?.[kind] || []).forEach((item) => {
      const prev = byCode.get(String(item.no));
      if (item.hasData) {
        byCode.set(String(item.no), applyDetailStanding({
          ...prev,
          ...item,
          technicalTeamScore: prev?.technicalTeamScore || item.technicalTeamScore,
          qualityAssuranceScore: prev?.qualityAssuranceScore || item.qualityAssuranceScore,
          petitionCommitteeScore: prev?.petitionCommitteeScore || item.petitionCommitteeScore,
        }, kind));
      } else if (!prev) {
        byCode.set(String(item.no), item);
      }
    });
    merged[kind] = [...byCode.values()].map((item) => applyDetailStanding(item, kind));
  });
  return merged;
}

function ciOutcomeLabel(fulfilled) {
  if (fulfilled == null) return "—";
  return fulfilled ? "Fulfilled" : "Not Fulfilled";
}

function CiOutcomeCell({ fulfilled }) {
  if (fulfilled == null) return "—";
  return fulfilled ? (
    <span style={{ color: "green", fontWeight: "bold" }}>Fulfilled</span>
  ) : (
    <span style={{ color: "#cf1322", fontWeight: "bold" }}>Not Fulfilled</span>
  );
}

function numericOrDash(value) {
  if (value == null || value === "") return "—";
  return value;
}

function roleCiText(item, field) {
  if (!item) return "—";
  const entered = readIndicatorManualScore(item, field);
  if (entered) return formatManualScoreDisplay(entered);
  const standing = resolveRoleStandingCiStatuses(item);
  const value =
    field === MANUAL_SCORE_FIELDS.TECHNICAL_TEAM
      ? standing.technicalStanding
      : field === MANUAL_SCORE_FIELDS.QUALITY_ASSURANCE
        ? standing.qualityAssuranceStanding
        : standing.petitionStanding;
  return ciOutcomeLabel(value);
}

function roleNumericText(item, field) {
  if (!item) return "—";
  const standing = resolveRoleStandingNumericScores(item);
  if (field === MANUAL_SCORE_FIELDS.TECHNICAL_TEAM) {
    return standing.technicalStanding ?? item.technicalTeamStandingScore ?? "—";
  }
  if (field === MANUAL_SCORE_FIELDS.QUALITY_ASSURANCE) {
    return standing.qualityAssuranceStanding ?? item.qualityAssuranceStandingScore ?? "—";
  }
  return standing.petitionStanding ?? item.petitionCommitteeStandingScore ?? "—";
}

function applyDetailStanding(item, kind) {
  if (!item) return item;
  if (kind === "CI") {
    const standing = resolveRoleStandingCiStatuses(item);
    return {
      ...item,
      systemFulfilled: standing.systemFulfilled,
      fulfilled: standing.finalFulfilled,
      technicalTeamStandingFulfilled: standing.technicalStanding,
      qualityAssuranceStandingFulfilled: standing.qualityAssuranceStanding,
      petitionCommitteeStandingFulfilled: standing.petitionStanding,
    };
  }
  const standing = resolveRoleStandingNumericScores(item);
  return {
    ...item,
    systemActualScore: standing.systemScore,
    systemScore: standing.systemScore,
    actualScore: standing.finalScore,
    technicalTeamStandingScore: standing.technicalStanding,
    qualityAssuranceStandingScore: standing.qualityAssuranceStanding,
    petitionCommitteeStandingScore: standing.petitionStanding,
  };
}

function cellValue(kind, item, layer) {
  if (!item || item.hasData === false) {
    return kind === "CI" ? { type: "ci", fulfilled: null } : { type: "text", text: "—" };
  }
  if (kind === "CI") {
    const standing = resolveRoleStandingCiStatuses(item);
    if (layer === "system") return { type: "ci", fulfilled: standing.systemFulfilled };
    if (layer === "final") return { type: "ci", fulfilled: standing.finalFulfilled };
    return { type: "text", text: roleCiText(item, layer) };
  }
  const standing = resolveRoleStandingNumericScores(item);
  if (layer === "system") return { type: "text", text: numericOrDash(standing.systemScore) };
  if (layer === "final") return { type: "text", text: numericOrDash(standing.finalScore) };
  return { type: "text", text: roleNumericText(item, layer) };
}

function renderCellValue(value) {
  if (!value) return "—";
  if (value.type === "ci") return <CiOutcomeCell fulfilled={value.fulfilled} />;
  return value.text ?? "—";
}

function resolveThematicArea(col, item, parentItems = []) {
  const fromItem = String(item?.thematicArea || "").trim();
  if (fromItem) return fromItem;
  const fromSlug = THEMATIC_BY_SLUG[col.slug];
  if (fromSlug) return fromSlug;
  const parent = (parentItems || []).find((row) => {
    const parsed = parseIndicatorCode(row?.no);
    return parsed?.type === "parent" && parsed.major === col.major;
  });
  return String(parent?.thematicArea || "").trim();
}

function numericFromCell(value) {
  const raw = value?.type === "text" ? value.text : value;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

function sumScoreColumn(rows, columnIndex) {
  return rows.reduce((sum, row) => {
    const n = numericFromCell(row.values[columnIndex]);
    return n == null ? sum : sum + n;
  }, 0);
}

function isFulfilledCell(value) {
  if (!value) return false;
  if (value.type === "ci") return value.fulfilled === true;
  return String(value.text || "").trim().toLowerCase() === "fulfilled";
}

function countFulfilledColumn(rows, columnIndex) {
  return rows.reduce((sum, row) => sum + (isFulfilledCell(row.values[columnIndex]) ? 1 : 0), 0);
}

function sectionTotalValue(kind, rows, columnIndex) {
  if (kind === "CI") {
    return `${countFulfilledColumn(rows, columnIndex)} / ${rows.length}`;
  }
  return sumScoreColumn(rows, columnIndex);
}

const SUB_COLUMNS = buildSubColumns();

const DpatSubIndicatorScoresSummary = ({
  district,
  districtId,
  year,
  hideRoleScoreColumns = false,
  assessmentStartTables = null,
}) => {
  const { user } = useAuth();
  const queryDistrictId = resolveDistrictQueryId(district, districtId);
  const { data: response, isLoading, error } = useGetScoreSheetSummary(
    queryDistrictId,
    year,
    "DPAT",
  );

  const summaryData = response?.data || response || {};
  const lists = useMemo(() => {
    const saved = {
      CI: summaryData.ciSubIndicators || [],
      SDI: summaryData.sdiSubIndicators || [],
      PI: summaryData.piSubIndicators || [],
    };
    const parentFallback = {
      CI: summaryData.complianceIndicators || [],
      SDI: summaryData.serviceDeliveryIndicators || [],
      PI: summaryData.performanceIndicators || [],
    };
    const live =
      assessmentStartTables && Object.keys(assessmentStartTables).length
        ? buildSubIndicatorDetailLists(summaryData, [], assessmentStartTables)
        : null;
    const merged = live ? mergeSubLists(saved, live) : saved;
    return {
      CI: merged.CI?.length ? merged.CI : parentFallback.CI,
      SDI: merged.SDI?.length ? merged.SDI : parentFallback.SDI,
      PI: merged.PI?.length ? merged.PI : parentFallback.PI,
    };
  }, [summaryData, assessmentStartTables]);

  const hasComplaintResolutionInput = useMemo(() => {
    const items = [
      ...(lists.CI || []),
      ...(lists.SDI || []),
      ...(lists.PI || []),
      ...(summaryData.complianceIndicators || []),
      ...(summaryData.serviceDeliveryIndicators || []),
      ...(summaryData.performanceIndicators || []),
    ];
    return items.some(itemHasComplaintResolutionInput);
  }, [lists, summaryData]);

  const roleColumnVisibility = useMemo(() => {
    if (hasDpatFullScoreAccess(user)) {
      return {
        technical: true,
        qualityAssurance: true,
        petition: hasComplaintResolutionInput,
      };
    }
    if (hideRoleScoreColumns || isDpatDistrictRestrictedViewer(user)) {
      return { technical: false, qualityAssurance: false, petition: false };
    }
    const roleNames = user?.user?.userRoles?.map((role) => role.name) ?? [];
    const isPetition = roleNames.includes("DPAT PETITION COMMITTEE") || roleNames.includes("Superuser");
    const isQa = roleNames.includes("DPAT QUALITY ASSURANCE");
    const isTechnical = roleNames.includes("DPAT TECHNICAL TEAM");
    if (isPetition) return { technical: true, qualityAssurance: true, petition: true };
    if (isQa) return { technical: true, qualityAssurance: true, petition: false };
    if (isTechnical) return { technical: true, qualityAssurance: false, petition: false };
    return { technical: false, qualityAssurance: false, petition: false };
  }, [hasComplaintResolutionInput, hideRoleScoreColumns, user]);

  const showSystemColumns =
    hasDpatFullScoreAccess(user) || !(hideRoleScoreColumns || isDpatDistrictRestrictedViewer(user));
  const districtLabel = district?.label || district?.name || "District";

  const scoreColumns = [
    showSystemColumns ? { key: "system", label: "System Score", layer: "system" } : null,
    roleColumnVisibility.technical
      ? { key: "technical", label: "Technical Team Score", layer: MANUAL_SCORE_FIELDS.TECHNICAL_TEAM }
      : null,
    roleColumnVisibility.qualityAssurance
      ? { key: "qa", label: "Quality Assurance Score", layer: MANUAL_SCORE_FIELDS.QUALITY_ASSURANCE }
      : null,
    roleColumnVisibility.petition
      ? { key: "petition", label: "Complaints Resolution Score", layer: MANUAL_SCORE_FIELDS.PETITION_COMMITTEE }
      : null,
    { key: "final", label: "Final Score", layer: "final" },
  ].filter(Boolean);

  const sectionMeta = {
    CI: { title: "SECTION A: COMPLIANCE INDICATORS (CI)" },
    SDI: { title: "SECTION B: SERVICE DELIVERY INDICATORS (SDIs)" },
    PI: { title: "SECTION C: PERFORMANCE INDICATORS (PIs)" },
  };

  const parentLists = {
    CI: summaryData.complianceIndicators || [],
    SDI: summaryData.serviceDeliveryIndicators || [],
    PI: summaryData.performanceIndicators || [],
  };

  const sectionRows = (kind) =>
    SUB_COLUMNS.filter((col) => col.kind === kind).map((col) => {
      const item = applyDetailStanding(findSubRow(lists[kind], col.subKey), kind);
      return {
        ...col,
        item,
        thematicArea: resolveThematicArea(col, item, parentLists[kind]),
        values: scoreColumns.map((scoreCol) => cellValue(kind, item, scoreCol.layer)),
      };
    });

  const ciRows = sectionRows("CI");
  const sdiRows = sectionRows("SDI");
  const piRows = sectionRows("PI");
  const headerColSpan = 2 + scoreColumns.length;

  if (isLoading && !response) {
    return null;
  }

  if (error) {
    return (
      <div className="text-center py-8 text-danger">
        Could not load sub-indicator scores.
      </div>
    );
  }

  return (
    <div>
      <div className="space-y-6">
        {[
          { kind: "CI", rows: ciRows },
          { kind: "SDI", rows: sdiRows },
          { kind: "PI", rows: piRows },
        ].map(({ kind, rows }) => (
          <div key={kind} className="table-responsive" data-pdf-part={`details-${kind}`}>
            {kind === "CI" ? (
              <div style={{ marginBottom: 12 }}>
                <h3 style={{ margin: 0, textAlign: "center", padding: "10px" }}>
                  DETAILS OF SUB-INDICATOR SCORES (CI, SDI, PI)
                </h3>
                <p style={{ margin: "6px 0 0", textAlign: "center", fontSize: 13 }}>
                  {districtLabel} — {year}
                </p>
              </div>
            ) : null}
            <table
            key={kind}
            className="table table-bordered"
            style={{ border: "1px solid #000", borderCollapse: "collapse", width: "100%" }}
          >
            <thead>
              <tr style={{ backgroundColor: "#ccccc9f2", fontWeight: "bold" }}>
                <th colSpan={headerColSpan} style={cellBorder}>{sectionMeta[kind].title}</th>
              </tr>
              <tr style={{ fontWeight: "bold" }}>
                <th style={{ ...cellBorder, whiteSpace: "nowrap" }}>No.</th>
                <th style={cellBorder}>Thematic Area</th>
                {scoreColumns.map((col) => (
                  <th key={`${kind}-${col.key}`} style={{ ...cellBorder, textAlign: "center", whiteSpace: "nowrap" }}>
                    {kind === "CI"
                      ? col.key === "system"
                        ? "System Outcome"
                        : col.key === "final"
                          ? "Final Outcome"
                          : col.label.replace(" Score", "")
                      : col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length ? rows.map((row, index) => {
                const prev = rows[index - 1];
                const showGroup = !prev || prev.parentLabel !== row.parentLabel;
                return (
                  <React.Fragment key={row.slug}>
                    {showGroup ? (
                      <tr style={{ backgroundColor: "#e8e8e6", fontWeight: "bold" }}>
                        <td colSpan={headerColSpan} style={cellBorder}>{row.parentLabel}</td>
                      </tr>
                    ) : null}
                    <tr>
                      <td style={{ ...cellBorder, whiteSpace: "nowrap" }}><strong>{row.label}</strong></td>
                      <td style={cellBorder}>{row.thematicArea || "—"}</td>
                      {row.values.map((value, valueIndex) => (
                        <td
                          key={`${row.slug}-${scoreColumns[valueIndex].key}`}
                          style={{
                            ...cellBorder,
                            textAlign: "center",
                            whiteSpace: "nowrap",
                            fontWeight: scoreColumns[valueIndex].key === "final" ? "bold" : undefined,
                          }}
                        >
                          {renderCellValue(value)}
                        </td>
                      ))}
                    </tr>
                  </React.Fragment>
                );
              }) : (
                <tr>
                  <td colSpan={headerColSpan} style={{ ...cellBorder, textAlign: "center" }}>
                    No {kind} sub-indicator scores
                  </td>
                </tr>
              )}
              {rows.length ? (
                <tr style={{ backgroundColor: "#f0f0f0", fontWeight: "bold" }}>
                  <td style={cellBorder} />
                  <td style={cellBorder}>{kind === "CI" ? "Total Fulfilled" : "Total"}</td>
                  {scoreColumns.map((col, index) => (
                    <td key={`${kind}-total-${col.key}`} style={{ ...cellBorder, textAlign: "center" }}>
                      {sectionTotalValue(kind, rows, index)}
                    </td>
                  ))}
                </tr>
              ) : null}
            </tbody>
          </table>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DpatSubIndicatorScoresSummary;
